/**
 * Read/write adapter for the Organisation settings screen (C6, #21937;
 * wired to the real production bearer client by C11, #22333) — mirrors
 * `wagepro_store_dashboard.ts` / `wagepro_store_pay_runs.ts`'s pure adapter
 * pattern exactly: `RealOrganisationRecord` is the permanent adapter shape
 * (survives the #21929 employer-admin OAuth scope work unchanged; only HOW
 * it gets resolved changes — the read is `use_session_facts.ts`'s shared
 * `entity.clientGet` fetch, the write is `use_settings.ts`'s
 * `saveRealOrganisationSettings`; the interim dev-bypass-only
 * `lib/auth/dev_settings.ts` resolver/executor this replaced was deleted by
 * C11), and `deriveOrganisationSettings` / `buildEntityUpdateInput` are the
 * permanent seams `wagepro_store.tsx` calls. Pure, no React, no network.
 *
 * ── Field mapping (verified against `apps/api/src/trpc/routers/entities.ts`,
 *    `updateEntitySchema` ~line 624, `update` ~line 1374, `clientGet` ~line
 *    1231) ────────────────────────────────────────────────────────────────
 *
 * - `legal_entity` (UI) <-> `name` (the real `entities.name` DB column —
 *   despite the column's generic name, this IS the entity's legal name;
 *   there is no separate `legal_entity` column).
 * - `base_currency` (UI) <-> `currency_code`. The backend LOCKS this once
 *   any journal exists for the entity (`entities.update` probes the tenant
 *   `journal_entries` table and throws `BAD_REQUEST: 'Cannot change base
 *   currency after transactions exist'` before writing anything if so) — see
 *   `use_settings.ts`'s `saveRealOrganisationSettings` for how that error is
 *   surfaced, and `buildEntityUpdateInput` below for why an
 *   unchanged `currency_code` is never sent at all. The backend itself only
 *   runs the probe when `input.currency_code` is BOTH present AND different
 *   from the entity's current value (`input.currency_code !==
 *   existingEntity.currency_code` — see `apps/api/src/trpc/routers/
 *   entities.ts`'s `update` procedure), so an unchanged value would NOT trip
 *   it even if sent; omitting it here is purely about keeping the wire delta
 *   minimal, not working around a probe gap that doesn't exist.
 * - `fiscal_year_start_month` (UI) <-> `financial_year_end` (real backend,
 *   month the fiscal year ENDS, 1-12) — a semantic inversion, not a naming
 *   difference. `apps/api/src/lib/period_key.ts`'s `canonicalPeriodKey`
 *   defines the authoritative formula: `fyStartMonth = (financialYearEndMonth
 *   % 12) + 1`. `fyEndMonthToStartMonth`/`fyStartMonthToEndMonth` below are
 *   that formula and its exact inverse.
 * - `locale` was DROPPED from the form entirely (C6, #21937) — see
 *   `types.ts`'s `OrganisationSettings` doc comment for why (no `entities`
 *   column, no consumer anywhere). Not represented in `RealOrganisationRecord`
 *   at all.
 */
import type { OrganisationSettings } from './types';

/**
 * The exact subset of an `entities` row this screen reads/writes — an
 * intentional subset (not a mirror of the full control-plane row), same
 * stance as `RealPayRunLine`/`RealFilingPeriod` elsewhere in this epic.
 */
export interface RealOrganisationRecord {
  name: string;
  /** Month 1-12 the fiscal year ENDS — the real backend's own unit. See module docblock. */
  financial_year_end: number;
  currency_code: string;
}

/**
 * The month the fiscal year STARTS, given the month it ENDS (both 1-12).
 * Matches `apps/api/src/lib/period_key.ts`'s `canonicalPeriodKey` formula
 * exactly: `(financialYearEndMonth % 12) + 1`. December (12) -> January (1);
 * March (3) -> April (4).
 */
export function fyEndMonthToStartMonth(endMonth: number): number {
  return (endMonth % 12) + 1;
}

/**
 * The exact inverse of {@link fyEndMonthToStartMonth}: the month the fiscal
 * year ENDS, given the month it STARTS (both 1-12). January (1) -> December
 * (12); April (4) -> March (3).
 */
export function fyStartMonthToEndMonth(startMonth: number): number {
  return startMonth === 1 ? 12 : startMonth - 1;
}

/**
 * Derives `OrganisationSettings` from `mock` plus the real entity record,
 * when resolved. `real` absent (the resolver failed, or hasn't run) leaves
 * `mock` UNCHANGED — same "mock unchanged when absent" contract as every
 * other adapter in this epic. `real` present derives every field from it
 * (there is no per-field independent fallback here, unlike
 * `wagepro_store_dashboard.ts` — the three organisation fields are all
 * sourced from the SAME single `entity.clientGet` call, so there is no
 * scenario where only some of them resolved).
 */
export function deriveOrganisationSettings(
  mock: OrganisationSettings,
  real: RealOrganisationRecord | undefined,
): OrganisationSettings {
  if (real === undefined) return mock;
  return {
    legal_entity: real.name,
    fiscal_year_start_month: fyEndMonthToStartMonth(real.financial_year_end),
    base_currency: real.currency_code,
  };
}

/**
 * The wire-shape fields `entities.update` accepts for this screen — a
 * strict subset of `updateEntitySchema` (`apps/api/src/trpc/routers/
 * entities.ts`). Deliberately does NOT include `firm_id`/`entity_id` — those
 * are entity-context concerns resolved by `use_settings.ts`'s
 * `saveRealOrganisationSettings`, never by this pure `data/`
 * module (mirrors `PayRunActionsController.start_pay_run`'s
 * `{ period_start, period_end }` domain-only input in `wagepro_store.tsx` —
 * ids are attached by the executor, not the pure delta builder).
 */
export interface OrganisationUpdateFields {
  name?: string;
  financial_year_end?: number;
  currency_code?: string;
}

/**
 * Builds the minimal `entities.update` delta for a save — a field is
 * included ONLY when `draft` actually differs from `loaded` (the real record
 * as last read/persisted). Note this is NOT working around a probe gap: the
 * backend only runs its journal-existence probe when `currency_code` is both
 * present AND different from the entity's current value (see module
 * docblock), so sending an unchanged `currency_code` would not, in fact,
 * trip it. This diffing is purely about keeping the wire payload minimal —
 * consistent with how `name`/`financial_year_end` are diffed too, and a
 * cheap defensive habit given the currency field carries a real backend
 * side effect (see module docblock's currency-lock note) even when it does
 * not itself lock.
 *
 * (An earlier version of this comment incorrectly claimed an unchanged
 * `currency_code` WOULD trip the probe — corrected, second-round
 * adversarial review, C11 #22333.)
 *
 * `financial_year_end` is compared in BACKEND units (via
 * {@link fyStartMonthToEndMonth}) — `draft.fiscal_year_start_month` is
 * always UI/start-month units, never compared or sent as a raw start month.
 */
export function buildEntityUpdateInput(
  loaded: RealOrganisationRecord,
  draft: OrganisationSettings,
): OrganisationUpdateFields {
  const input: OrganisationUpdateFields = {};

  if (draft.legal_entity !== loaded.name) {
    input.name = draft.legal_entity;
  }

  const draftFinancialYearEnd = fyStartMonthToEndMonth(
    draft.fiscal_year_start_month,
  );
  if (draftFinancialYearEnd !== loaded.financial_year_end) {
    input.financial_year_end = draftFinancialYearEnd;
  }

  if (draft.base_currency !== loaded.currency_code) {
    input.currency_code = draft.base_currency;
  }

  return input;
}

/**
 * What `wagepro_store.tsx`'s `update_organisation` must do for a given save
 * attempt — extracted as a pure, directly-testable function (no React, no
 * network), mirroring `use_pay_runs.ts`'s `resolvePayRunsReadState` "pure
 * decision function + thin hook/store" split (CLAUDE.md's Component
 * Testability rule).
 *
 * Added because two behaviors this PR's OWN first-round adversarial-review
 * self-review claimed to have fixed — the `'refuse'` branch below (see PR
 * body: "Fabricated-success hardening") and the `'noop'` branch (the
 * empty-delta fast path) — shipped with ZERO test coverage anywhere: no test
 * in this app renders `WageProStoreProvider`, so a mutation deleting either
 * fix would leave the full suite green (second-round adversarial-review
 * finding, C11 #22333).
 *
 * - `'refuse'` — a real `organisation_actions` controller is wired with NO
 *   `loaded` record. Structurally unreachable today (`main.tsx`'s
 *   `hasRealOrganisation` guard, `use_settings.ts`'s
 *   `resolveOrganisationActions`, only ever wires both together), but a
 *   defensive refusal rather than silently falling through to the mock
 *   "always succeeds" path, which would fabricate a real save's success when
 *   nothing was ever sent to the API.
 * - `'noop'` — a real controller IS wired and a record IS loaded, but
 *   `draft` has no actual delta against it (`buildEntityUpdateInput` returns
 *   `{}`) — no real mutation to make. Mirrors the mock path's "always
 *   succeeds" contract rather than sending an empty update.
 * - `'send'` — a real controller is wired, a record is loaded, and the delta
 *   is non-empty — the caller must actually round-trip
 *   `organisation_actions.save(delta)`.
 * - `'mock'` — no real controller is wired at all (plain mock mode, or a
 *   test harness mounting `WageProStoreProvider` directly) — the existing
 *   synchronous-resolving mock setter path, unaffected.
 */
export type OrganisationSaveDecision =
  | { kind: 'refuse' }
  | { kind: 'noop'; record: RealOrganisationRecord }
  | { kind: 'send'; delta: OrganisationUpdateFields }
  | { kind: 'mock' };

export function planOrganisationSave(
  hasOrganisationActions: boolean,
  loaded: RealOrganisationRecord | undefined,
  draft: OrganisationSettings,
): OrganisationSaveDecision {
  if (hasOrganisationActions && !loaded) {
    return { kind: 'refuse' };
  }
  if (hasOrganisationActions && loaded) {
    const delta = buildEntityUpdateInput(loaded, draft);
    if (Object.keys(delta).length === 0) {
      return { kind: 'noop', record: loaded };
    }
    return { kind: 'send', delta };
  }
  return { kind: 'mock' };
}

/** `PayRun` statuses that certainly imply a posted journal exists for this entity — see this module's docblock on the currency lock. */
const STATUSES_WITH_POSTED_JOURNALS = new Set(['approved', 'paid']);

/**
 * `true` when any already-resolved REAL pay run has posted (`'approved'` or
 * `'paid'`) — a real journal certainly exists for this entity, so the
 * base-currency field should be disabled pre-emptively rather than waiting
 * for a save attempt to fail against the backend's journal-existence probe.
 * Takes a bare `{ status: string }[]` (not the full `PayRun` type) so this
 * stays a tiny, dependency-free predicate.
 *
 * Callers MUST pass real records only (e.g. `RealPayRunRecord[]`, or `[]`
 * when none have resolved yet) — never the store's mock-fallback `pay_runs`
 * UI list, which defaults to `MOCK_PAY_RUNS` (itself containing `'paid'`
 * runs) whenever the real records are `undefined`. Passing the mock-fallback
 * list here would permanently disable the currency field in plain mock mode,
 * and would incorrectly lock a freshly-resolved REAL entity's currency field
 * based on fictional mock history whenever the pay-run READ path itself
 * degrades to mock while the organisation-settings READ path stays real (a
 * real, documented mixed state — see `wagepro_store.tsx`'s `has_posted_pay_run`
 * doc comment; adversarial review finding, C6 #21937).
 *
 * Deliberately does NOT count `'voided'` as posted, even though the backend's
 * journal-existence probe (`entities.update`) checks ROW EXISTENCE, not net
 * effect — a voided run's reversal entry means both the original and the
 * reversing journal rows still exist, so the backend WOULD still reject a
 * currency change against a voided-only history. This function is a
 * deliberate pre-emptive UNDER-approximation (UX affordance only), always
 * backstopped by the reactive `currency_locked` path
 * (`saveRealOrganisationSettings`) for any case it under-counts.
 */
export function hasPostedPayRun(
  payRuns: readonly { status: string }[],
): boolean {
  return payRuns.some((run) => STATUSES_WITH_POSTED_JOURNALS.has(run.status));
}
